import KitIcon, { IconName } from '@omni/kit/components/KitIcon';
import KitLoader from '@omni/kit/components/KitLoader';
import Durations from '@omni/kit/constants/Durations';
import { createAnimation } from '@omni/kit/theming/Animating';
import Colors from '@omni/kit/theming/Colors';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Animated, Easing, Platform, StyleSheet, View } from 'react-native';

export type Actions = 'play' | 'pause' | 'skip-forward' | 'skip-backward';

export interface ActionIndicatorRef {
  showAction(actionToShow: Actions): void;
  clearAction(): void;
}

export interface ActionIndicatorProps {
  isLoading?: boolean;
  onlyShowLoading?: boolean;
}

const iconMap: Record<Actions, IconName> = {
  play: 'play',
  pause: 'pause',
  'skip-forward': 'skip-forward-10',
  'skip-backward': 'skip-backward-10',
};

const FlashIconPause = 400;
const ScaleFrom = 0.95;
const ScaleTo = 1;
const ScaleDuration = Durations.l;
const FadeDuration = Durations.s;

/**
 * ActionIndicator is used through its `ref` by calling `showAction(name)`.
 * @example
 * `actionIndicatorRef.current?.showAction('play');`
 *
 * You can optionall pass the `holdAction` option to keep the action visible
 * until another action is shown, or `clearAction` is called.
 */
const ActionIndicator = React.forwardRef<
  ActionIndicatorRef,
  ActionIndicatorProps
>(({ isLoading, onlyShowLoading }, actionIndicatorRef) => {
  const FlashIconTimer = useRef<ReturnType<typeof setTimeout>>(null);
  const scale = useRef(new Animated.Value(ScaleFrom)).current;
  const fade = useRef(new Animated.Value(0)).current;

  const [action, setAction] = useState<Actions>();
  const [isShowingAction, setIsShowingAction] = useState(false);
  const icon = action && iconMap[action];

  const flashInAnim = useRef(
    Animated.parallel([
      createAnimation(scale, ScaleTo, ScaleDuration, Easing.elastic(1)),
      createAnimation(fade, 1, FadeDuration, Easing.ease),
    ])
  ).current;
  const flashOutAnim = useRef(
    Animated.parallel([
      createAnimation(scale, ScaleFrom, ScaleDuration, Easing.elastic(1)),
      createAnimation(fade, 0, FadeDuration, Easing.ease),
    ])
  ).current;

  const flashIn = useCallback(() => {
    flashInAnim.reset();
    /**
     * Start the value at something a bit higher for the fade-in. This
     * prevents a big opacity shift from 0-1 when called multiple times
     * quickly.
     */
    fade.setValue(0.7);
    flashInAnim.start();
  }, [fade, flashInAnim]);

  useEffect(() => {
    if (isLoading) {
      flashIn();
    } else {
      flashOutAnim.start();
    }
  }, [flashIn, flashOutAnim, isLoading]);

  useImperativeHandle(actionIndicatorRef, () => ({
    showAction(actionToShow) {
      if (onlyShowLoading) return;

      if (FlashIconTimer.current) {
        clearTimeout(FlashIconTimer.current);
      }

      setAction(actionToShow);
      setIsShowingAction(true);
      flashIn();

      FlashIconTimer.current = setTimeout(() => {
        setIsShowingAction(false);
        if (!isLoading) {
          flashOutAnim.start();
        }
      }, FlashIconPause);
    },
    clearAction() {
      if (!isLoading) {
        flashOutAnim.start();
      }
    },
  }));

  return (
    <Animated.View
      style={[styles.container, { opacity: fade, transform: [{ scale }] }]}
    >
      <KitIcon style={styles.gradient} name='indicator-protector' size={200} />
      <View>
        {isShowingAction ? (
          <KitIcon color={Colors.N0} size={44} name={icon} />
        ) : isLoading ? (
          <KitLoader size={Platform.isTV ? 56 : 36} white />
        ) : null}
      </View>
    </Animated.View>
  );
});

const containerSize = 84;

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    display: 'flex',
    height: containerSize,
    justifyContent: 'center',
    width: containerSize,
  },
  gradient: {
    position: 'absolute',
  },
});

export default ActionIndicator;
